/**
 * store/api/step-factories/index.ts
 * desc: RTK Query endpoints for step factories and their versions (/api/v1/step-factories), used by the AI version
 *       page; the docker pages keep their thunks in store/actions/step-factories
 * note: an update is the backend's full version POST, not the thunk's JSON patch — the AI configuration round-trips
 *       updatedAt for the stale check and its settings are a list. The saved version is written straight into the
 *       cache so the page keeps the fresh updatedAt; a create invalidates the factory, whose versionIds changed
 */
import { api } from '../index';
import apiUrls from '../../../settings/api-urls';
import { interpolate } from '../../../utilities';
import type { StepFactoryCreationRequest, StepFactoryDto, StepFactoryVersionCreationRequest, StepFactoryVersionDto } from '../../../types/api/step-factories';
import type { JsonPatchOperation } from '../../../types/api/json-patch';

export interface StepFactoryVersionRef {
    stepFactoryId: string;
    stepFactoryVersionId: string;
}

export interface CreateStepFactoryVersionArgs {
    stepFactoryId: string;
    body: StepFactoryVersionCreationRequest;
}

export interface UpdateStepFactoryVersionArgs extends StepFactoryVersionRef {
    body: StepFactoryVersionDto;
}

export interface UpdateStepFactoryArgs {
    stepFactoryId: string;
    body: JsonPatchOperation[];
}

const FACTORY_TAG = 'STEP_FACTORY' as const;
const VERSION_TAG = 'STEP_FACTORY_VERSION' as const;
const VERSIONS_TAG = 'STEP_FACTORY_VERSIONS' as const;

export const stepFactoriesApi = api.injectEndpoints({
    endpoints: (build) => ({
        getStepFactory: build.query<StepFactoryDto, string>({
            query: (stepFactoryId) => ({
                url: interpolate(apiUrls.STEP_FACTORY, { stepFactoryId }),
                method: 'GET',
            }),
            providesTags: (result, error, stepFactoryId) => [ { type: FACTORY_TAG, id: stepFactoryId } ],
        }),
        getStepFactoryVersion: build.query<StepFactoryVersionDto, StepFactoryVersionRef>({
            query: ({ stepFactoryId, stepFactoryVersionId }) => ({
                url: interpolate(apiUrls.STEP_FACTORY_VERSION, { stepFactoryId, stepFactoryVersionId }),
                method: 'GET',
            }),
            providesTags: (result, error, { stepFactoryVersionId }) => [ { type: VERSION_TAG, id: stepFactoryVersionId } ],
        }),
        createStepFactory: build.mutation<StepFactoryDto, StepFactoryCreationRequest>({
            query: (body) => ({
                url: apiUrls.STEP_FACTORIES,
                method: 'POST',
                body,
            }),
        }),
        updateStepFactory: build.mutation<StepFactoryDto, UpdateStepFactoryArgs>({
            query: ({ stepFactoryId, body }) => ({
                url: interpolate(apiUrls.STEP_FACTORY, { stepFactoryId }),
                method: 'PATCH',
                body,
            }),
            onQueryStarted: ({ stepFactoryId }, { dispatch, queryFulfilled }) => queryFulfilled.then(({ data }) => {
                dispatch(stepFactoriesApi.util.upsertQueryData('getStepFactory', stepFactoryId, data));
            }, () => { /* surfaced by the base query; the cached factory stays as it was */ }),
        }),
        getStepFactoryVersions: build.query<StepFactoryVersionDto[], string>({
            query: (stepFactoryId) => ({
                url: interpolate(apiUrls.STEP_FACTORY_VERSIONS, { stepFactoryId }),
                method: 'GET',
            }),
            providesTags: (result, error, stepFactoryId) => [ { type: VERSIONS_TAG, id: stepFactoryId } ],
        }),
        deleteStepFactoryVersion: build.mutation<void, StepFactoryVersionRef>({
            query: ({ stepFactoryId, stepFactoryVersionId }) => ({
                url: interpolate(apiUrls.STEP_FACTORY_VERSION, { stepFactoryId, stepFactoryVersionId }),
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { stepFactoryId }) => (error ? [] : [ { type: FACTORY_TAG, id: stepFactoryId }, { type: VERSIONS_TAG, id: stepFactoryId } ]),
        }),
        createStepFactoryVersion: build.mutation<StepFactoryVersionDto, CreateStepFactoryVersionArgs>({
            query: ({ stepFactoryId, body }) => ({
                url: interpolate(apiUrls.STEP_FACTORY_VERSIONS, { stepFactoryId }),
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error, { stepFactoryId }) => (error ? [] : [ { type: FACTORY_TAG, id: stepFactoryId }, { type: VERSIONS_TAG, id: stepFactoryId } ]),
        }),
        updateStepFactoryVersion: build.mutation<StepFactoryVersionDto, UpdateStepFactoryVersionArgs>({
            query: ({ stepFactoryId, stepFactoryVersionId, body }) => ({
                url: interpolate(apiUrls.STEP_FACTORY_VERSION, { stepFactoryId, stepFactoryVersionId }),
                method: 'POST',
                body,
            }),
            onQueryStarted: ({ stepFactoryId, stepFactoryVersionId }, { dispatch, queryFulfilled }) => queryFulfilled.then(({ data }) => {
                dispatch(stepFactoriesApi.util.upsertQueryData('getStepFactoryVersion', { stepFactoryId, stepFactoryVersionId }, data));
            }, () => { /* surfaced by the base query; the cached version stays as it was */ }),
            // the list shows each version's model, which a save may change
            invalidatesTags: (result, error, { stepFactoryId }) => (error ? [] : [ { type: VERSIONS_TAG, id: stepFactoryId } ]),
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetStepFactoryQuery,
    useGetStepFactoryVersionQuery,
    useGetStepFactoryVersionsQuery,
    useCreateStepFactoryMutation,
    useUpdateStepFactoryMutation,
    useDeleteStepFactoryVersionMutation,
    useCreateStepFactoryVersionMutation,
    useUpdateStepFactoryVersionMutation,
} = stepFactoriesApi;

export default stepFactoriesApi;
